/** A page number, or "gap" for an ellipsis standing in for the pages between its neighbors. */
export type PageItem = number | "gap";

/** The width of a line of page buttons. */
export const pagesWidth = (items: PageItem[], width: (item: PageItem) => number, gap: number) =>
  items.reduce<number>((sum, item) => sum + width(item), 0) + gap * (items.length - 1);

/**
 * The page buttons that fit on one line: every page when they all fit; otherwise as many of the first
 * and the last pages as fit, with an ellipsis between them. The current page always shows, so a page
 * in the hidden middle sits between two ellipses. `width` gives an item's width and `gap` the space
 * between items (the same unit as `available`). When even the first, current and last pages don't
 * fit, they're returned anyway.
 */
export function fitPages(
  pages: number,
  current: number,
  width: (item: PageItem) => number,
  gap: number,
  available: number,
): PageItem[] {
  const span = (items: PageItem[]) => pagesWidth(items, width, gap);
  const all = Array.from({ length: pages }, (_, i) => i + 1);
  if (span(all) <= available) return all;

  const shown = new Set([1, pages, Math.min(Math.max(current, 1), pages)]);
  const items = () => {
    const sorted = [...shown].sort((a, b) => a - b);
    return sorted.flatMap((p, i): PageItem[] => (i > 0 && p - sorted[i - 1]! > 1 ? ["gap", p] : [p]));
  };
  // Add pages from the start and the end in turn, while the line still fits.
  let front = 2;
  let back = pages - 1;
  for (let fromFront = true, stuck = 0; front <= back && stuck < 2; fromFront = !fromFront) {
    const next = fromFront ? front : back;
    if (shown.has(next)) {
      if (fromFront) front++;
      else back--;
      continue;
    }
    shown.add(next);
    if (span(items()) <= available) {
      if (fromFront) front++;
      else back--;
      stuck = 0;
    } else {
      shown.delete(next);
      stuck++;
    }
  }
  return items();
}
